import * as React from 'react';

import { DirectionalHint, getId } from '@fluentui/react';
import { RefObjectFunction, useMergedRefs } from '@fluentui/react-hooks';

import { AppTooltipHost, getTooltipText, useAppTooltipHost } from '@1js/acui-tooltip';

/**
 * Returns the given ref merged with a local ref controlling a new AppTooltipHost instance
 * initialized with the given tooltip parameters. Once the returned ref is attached to your
 * button component, the button will have a tooltip. TooltipHostRef is also provided when
 * it is needed to call TooltipHost's imperative APIs.
 */
export const useAppButtonTooltips = (
  ref: React.Ref<any>,
  opts?: {
    customTooltip?: string;
    hideLabel?: boolean;
    id?: string;
    label?: string;
    direction?: DirectionalHint;
  }
): {
  mergedRef: RefObjectFunction<HTMLElement | null | undefined>;
  tooltipHostRef: React.MutableRefObject<AppTooltipHost | undefined>;
} => {
  const { customTooltip, hideLabel, id, label = '', direction } = opts || {};
  const tooltipId = React.useMemo(() => id || getId('tooltip-'), [id]);
  const tooltipText = getTooltipText(hideLabel, label, customTooltip);
  const { tooltipRef, tooltipHostRef } = useAppTooltipHost(tooltipId, { label: tooltipText, direction });

  const mergedRef = useMergedRefs<HTMLElement | null | undefined>(ref, tooltipRef);
  return { mergedRef, tooltipHostRef };
};
